import { createRoute, lazyRouteComponent, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { canvasEnabled, jobEntryDestination, onboardingOn } from "domains/aiChat/lib/takeover";
import { readTakeoverFlags } from "domains/aiChat/lib/takeover-flags";
import { requireLocationManager } from "domains/auth/access/locationManager";
import { homePathFor, isOnsiteManager } from "domains/auth/access/roles";
import { MARKETPLACE_DASHBOARD_NAMES } from "domains/dashboards/constants/templates";
import { queries as financeIndexQueries } from "domains/dashboards/views/FinanceIndex/queries";
import { verifyPunchSearchSchema } from "domains/gigs/containers/VerifyTimePunchPanel/routing";
import { hasJobsAccess, isJobsRolledOut } from "domains/hire/access/jobsAccess";
import { isScheduleInterviewsEnabled } from "domains/hire/hooks/useScheduleInterviewsEnabled";
import { queries as overhaulHomeQueries } from "domains/home/views/Home/queries";
import { postShiftMethods } from "domains/postGig/constants";
import { queries as postGigDialogQueries } from "domains/postGig/containers/PostGigDialog/queries";
import { postingV2On, postingV2SwitchedOnNow, postShiftDestination } from "domains/postGig/postingV2";
import { queries as postGigQueries } from "domains/postGig/views/Root/queries";
import { gigStatusOptionIds, gigTypeOptionIds } from "domains/schedule/containers/Filters/constants";
import { gigFilterSchema } from "domains/schedule/containers/Filters/routing";
import { queries as osmScheduleQueries } from "domains/schedule/containers/OnSiteManager/queries";
import { queries as historyQueries } from "domains/schedule/views/History/queries";
import { queries as payQueries } from "domains/schedule/views/Pay/queries";
import { queries as scheduleQueries } from "domains/schedule/views/Schedule/queries";
import { getFeatureFlag } from "providers/LaunchDarkly/client";
import React from "react";
import { omit, pick } from "remeda";
import { warmQueries } from "routing/warmQueries";
import { queries as homeQueries } from "views/Home/queries";
import { applicantsSearchSchema } from "views/ListApplicants/routing";
import * as z from "zod/mini";
import { locationLayoutRoute } from "./locations";

// Lazy so the heavy post-gig dependency subtree stays code-split out of the eager router module.
const PostGigDialog = React.lazy(() =>
  import("domains/postGig/containers/PostGigDialog").then((m) => ({ default: m.PostGigDialog })),
);

// Shared by schedule / pay / history (gig filters + calendar + sidebar) and the globally-mounted
// PostGigDialog (post_gig_*). All optional with no schema defaults so a filter toolbar can clear a
// field to undefined; display defaults live at the read sites instead.
export const marketplaceSearchSchema = z.extend(gigFilterSchema, {
  // view + calendar
  view: z.optional(z.enum(["list", "calendar"])),
  calendar_date: z.optional(z.coerce.date()),
  calendar_view: z.optional(z.enum(["month", "week", "day"])),
  calendar_mode: z.optional(z.enum(["view", "build-event"])),
  events: z.optional(z.array(z.enum(["gigs", "events"]))),
  interviews: z.optional(z.array(z.enum(["working", "phone", "video", "in_person"]))),
  interview_id: z.optional(z.string()),
  statuses: z.optional(z.array(z.enum([gigStatusOptionIds[0], ...gigStatusOptionIds]))),
  types: z.optional(z.array(z.enum([gigTypeOptionIds[0], ...gigTypeOptionIds]))),
  positions: z.optional(z.array(z.number())),
  managed: z.optional(z.boolean()),

  // gig info sidebar
  selected_gig_id: z.optional(z.number()),
  selected_pro_id: z.optional(z.number()),

  // post-gig dialog / flow
  return_to: z.optional(z.string()),
  post_gig_date: z.optional(z.coerce.date()),
  post_gig_type: z.optional(z.enum([postShiftMethods[0], ...postShiftMethods])),
  event_name: z.optional(z.string()),
  event_id: z.optional(z.string()),
  copy_from_gig_id: z.optional(z.number()),
  copy_weekdays: z.optional(z.array(z.int().check(z.gte(0), z.lte(6)))),
  offer_pro: z.optional(z.number()),
  offer_group: z.optional(z.string()),
  offer_candidate_id: z.optional(z.string()),
  offer_job_posting_id: z.optional(z.string()),
  // V2's way to today's single-shift form (its recurring hand-off): never plain, so it never opens V2 again.
  classic: z.optional(z.boolean()),
});

const timeAndAttendanceSearchSchema = z.object({
  name: z.optional(z.string()),
  attendance: z._default(z.enum(["all", "not-clocked", "clocked-in"]), "all"),
  activeOnly: z._default(z.boolean(), false),
  ...verifyPunchSearchSchema,
});

const eventTimeAndAttendanceSearchSchema = z.extend(timeAndAttendanceSearchSchema, {
  groupBy: z._default(z.enum(["start-end", "position"]), "start-end"),
  position: z.optional(z.string()),
});

type MarketplaceSearch = z.infer<typeof marketplaceSearchSchema>;

const postGigSearchKeys = [
  "return_to",
  "post_gig_date",
  "post_gig_type",
  "event_name",
  "event_id",
  "copy_from_gig_id",
  "copy_weekdays",
  "offer_pro",
  "offer_group",
  "offer_candidate_id",
  "offer_job_posting_id",
  "classic",
] as const;

/** Nothing in the link but the shift type: only that opens V2. A copy, date, offer, event or assignment keeps today's form. */
export const isPlainSingle = (search: MarketplaceSearch) =>
  (search.post_gig_type ?? "single") === "single" &&
  postGigSearchKeys.every((key) => key === "post_gig_type" || search[key] === undefined);

const postGigDeps = ({ search }: { search: MarketplaceSearch }) =>
  pick(search, ["post_gig_type", "copy_from_gig_id", "offer_pro", "offer_group"]);

const gigViewDeps = ({ search }: { search: MarketplaceSearch }) => omit(search, postGigSearchKeys);

export type EventTimeAndAttendanceSearch = z.infer<typeof eventTimeAndAttendanceSearchSchema>;

function MarketplaceLayout() {
  const { pathname } = useLocation();
  // The post-a-shift dialog opens whenever post_gig_type is in the URL; the standalone full-page
  // GIG_ADD route also drives itself off that param, so suppress the shared dialog there to avoid
  // rendering it on top of the full page. V2 too: the dialog it replaced must not animate closed as today's form.
  const onGigAdd = pathname.endsWith("/gigs-add") || pathname.endsWith("/gigs-add/v2");

  return (
    <>
      <Outlet />
      {!onGigAdd && (
        <React.Suspense fallback={null}>
          <PostGigDialog />
        </React.Suspense>
      )}
    </>
  );
}

export const marketplaceLayoutRoute = createRoute({
  getParentRoute: () => locationLayoutRoute,
  id: "marketplace",
  validateSearch: marketplaceSearchSchema,
  // The dialog's plain Shift opens V2 before anything renders; the search goes first, so filters never wait on flags.
  beforeLoad: async ({ context, params, search }) => {
    if (search.post_gig_type !== "single" || !isPlainSingle(search)) return;
    if (isOnsiteManager(context.auth.user, Number(params.locationId))) return;
    if (await postingV2On(context.queryClient, context.auth.user, Number(params.locationId))) {
      throw redirect({ to: "/locations/$locationId/gigs-add/v2", params, replace: true });
    }
  },
  loaderDeps: postGigDeps,
  loader: ({ deps, context }) => {
    if (context.location)
      warmQueries(context.queryClient, postGigDialogQueries({ location: context.location, search: deps }));
  },
  component: MarketplaceLayout,
});

// Per-location index: `/locations/$locationId` with nothing further — bounce to the role-aware home.
const indexRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  path: "/",
  beforeLoad: ({ context, params }) => {
    throw redirect({ href: homePathFor(context.auth.user, Number(params.locationId)) });
  },
});

// Shared between LM + OSM
export const timeAndAttendanceRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  path: "time-and-attendance/$date",
  validateSearch: eventTimeAndAttendanceSearchSchema,
  component: lazyRouteComponent(
    () => import("domains/gigs/views/MultiShiftTimeAndAttendance"),
    "MultiShiftTimeAndAttendanceView",
  ),
});

export const gigsTimeAndAttendanceRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  path: "gigs/$gigId/time-and-attendance",
  validateSearch: timeAndAttendanceSearchSchema,
  component: lazyRouteComponent(
    () => import("domains/gigs/views/ShiftTimeAndAttendance"),
    "ShiftTimeAndAttendanceView",
  ),
});

export const eventTimeAndAttendanceRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  path: "event/$eventId/time-and-attendance",
  validateSearch: eventTimeAndAttendanceSearchSchema,
  component: lazyRouteComponent(
    () => import("domains/gigs/views/EventTimeAndAttendance"),
    "EventTimeAndAttendanceView",
  ),
});

// Role-dispatched at loader level (OSM vs LM see a different Schedule implementation at the same path).
export const scheduleRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  path: "schedule",
  // `tab` is OSM-only; keep it optional (no schema default) so the LM schedule URL stays param-free.
  // OSM defaults it to "upcoming" at the read site.
  validateSearch: z.object({ tab: z.optional(z.enum(["upcoming", "past"])) }),
  loaderDeps: gigViewDeps,
  loader: async ({ context, params, deps, cause }) => {
    const now = new Date();
    const warm = cause !== "stay";
    if (isOnsiteManager(context.auth.user, Number(params.locationId))) {
      if (warm)
        warmQueries(context.queryClient, osmScheduleQueries({ search: deps, locationId: context.location?.id, now }));
      const { OnSiteManagerSchedule } = await import("domains/schedule/containers/OnSiteManager");
      return { Component: OnSiteManagerSchedule };
    }
    if (warm) {
      // Not awaited: a loader only warms, and flags would hold the pending screen.
      void Promise.all([
        getFeatureFlag("jobs-in-main-menu", { queryClient: context.queryClient }),
        getFeatureFlag("jobs-phased", { queryClient: context.queryClient }),
      ]).then(([jobsInMainMenu, jobsPhased]) =>
        warmQueries(
          context.queryClient,
          scheduleQueries({
            search: deps,
            location: context.location,
            now,
            isAdmin: !!context.auth.user.is_gigpro_admin,
            includeWorkingInterviews: isScheduleInterviewsEnabled(
              jobsInMainMenu,
              hasJobsAccess({
                jobsPhased,
                jobsEnabled: context.location?.jobs_enabled,
                user: context.auth.user,
                locationId: Number(params.locationId),
              }),
            ),
          }),
        ),
      );
    }
    const { Schedule } = await import("domains/schedule/views/Schedule");
    return { Component: Schedule };
  },
  component: function ScheduleRoute() {
    const { Component } = scheduleRoute.useLoaderData();
    return <Component />;
  },
});

// LM-only — OSM users are redirected to their schedule.
const lmOnlyRoute = createRoute({
  getParentRoute: () => marketplaceLayoutRoute,
  id: "marketplaceLmOnly",
  beforeLoad: ({ context, params }) => requireLocationManager(context.auth.user, Number(params.locationId)),
});

export const homeRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "home",
  validateSearch: z.object({
    show_tour: z.optional(z.boolean()),
    show_video: z.optional(z.boolean()),
    shifts_mine: z.optional(z.boolean()),
    jobs_mine: z.optional(z.boolean()),
  }),
  loaderDeps: ({ search }) => ({ shifts_mine: search.shifts_mine, jobs_mine: search.jobs_mine }),
  loader: async ({ context, cause, deps }) => {
    const parts = await getFeatureFlag("marketplace-overhaul", { queryClient: context.queryClient });
    const overhaul = Array.isArray(parts) && parts.includes("home");
    if (cause !== "stay" && context.location) {
      const input = { location: context.location, now: new Date() };
      warmQueries(context.queryClient, overhaul ? overhaulHomeQueries({ ...input, search: deps }) : homeQueries(input));
    }
    const { Home } = await (overhaul ? import("domains/home/views/Home") : import("views/Home"));
    return { Component: Home };
  },
  component: function HomeRoute() {
    const { Component } = homeRoute.useLoaderData();
    return <Component />;
  },
});

// The onboarding landing (the path tiles and the first-post redirect). Its old `start` requests open the canvas routes.
export const getStartedRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "get-started",
  validateSearch: z.object({
    start: z.optional(z.enum(["role", "job"])),
    position: z.optional(z.string()),
    copy_of: z.optional(z.int()),
  }),
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const { start, ...canvas } = search;
    if (start === "role") throw redirect({ to: "/locations/$locationId/post/shift", params, search: canvas });
    if (start === "job") {
      throw redirect({ to: "/locations/$locationId/post/job", params, search: { position: canvas.position } });
    }
    // The tiles landing is onboarding's; the canvases it links to are the chat's.
    if (!onboardingOn(await readTakeoverFlags(queryClient))) {
      throw redirect({ to: "/locations/$locationId/home", params });
    }
  },
  component: lazyRouteComponent(() => import("domains/aiChat/views/Takeover"), "TakeoverView"),
});

// Where every "post a shift" lands (`postShiftDestination`), reloads and bookmarks included. The canvas the chat opens
// to post (`entry: "dock"`, continuing the dock's conversation), or a direct visit, which is an onboarding-style entry
// with a conversation of its own. Optionally for a position, or as a copy of a shift.
export const postShiftRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "post/shift",
  validateSearch: z.object({
    position: z.optional(z.string()),
    copy_of: z.optional(z.int()),
    entry: z.optional(z.enum(["dock"])),
  }),
  beforeLoad: async ({ context: { queryClient, auth }, params, search }) => {
    const [takeover, optIn, postingPage] = await Promise.all([
      readTakeoverFlags(queryClient),
      getFeatureFlag("agent-web-posting-opt-in", { queryClient }),
      getFeatureFlag("agent-web-posting", { queryClient }),
    ]);
    const destination = postShiftDestination({
      canvas: canvasEnabled(takeover),
      optIn,
      postingPage,
      switchedOn: postingV2SwitchedOnNow(auth.user?.id),
      onsite: isOnsiteManager(auth.user, Number(params.locationId)),
    });
    if (destination === "v2") {
      const { position, copy_of } = search;
      throw redirect({
        to: "/locations/$locationId/gigs-add/v2",
        params,
        search: { position, copy_of },
        replace: true,
      });
    }
    if (destination === "form") throw redirect({ to: "/locations/$locationId/gigs-add", params });
  },
  component: lazyRouteComponent(() => import("domains/aiChat/views/Takeover"), "PostShiftView"),
});

// The job canvas: an explicit request to post a job, optionally for the position the manager named. Unlike the
// onboarding landing it ignores the first post and Skip to Home: the manager asked for it.
export const postJobRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "post/job",
  validateSearch: z.object({ position: z.optional(z.string()), entry: z.optional(z.enum(["dock"])) }),
  beforeLoad: async ({ context: { queryClient, location }, params }) => {
    const jobsPhased = await getFeatureFlag("jobs-phased", { queryClient });
    const destination = jobEntryDestination({
      enabled: canvasEnabled(await readTakeoverFlags(queryClient)),
      jobsAccess: isJobsRolledOut({ jobsPhased, jobsEnabled: location?.jobs_enabled }),
    });
    if (destination === "hire-form") {
      throw redirect({ to: "/locations/$locationId/hire/jobs", params, search: { new: true } });
    }
    if (destination === "home") throw redirect({ to: "/locations/$locationId/home", params });
  },
  component: lazyRouteComponent(() => import("domains/aiChat/views/Takeover"), "PostJobView"),
});

const payRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "pay",
  loaderDeps: gigViewDeps,
  loader: ({ deps, context, cause }) => {
    if (cause === "stay") return;
    warmQueries(
      context.queryClient,
      payQueries({
        search: deps,
        location: context.location,
        now: new Date(),
        isAdmin: !!context.auth.user.is_gigpro_admin,
      }),
    );
  },
  component: lazyRouteComponent(() => import("domains/schedule/views/Pay"), "Pay"),
});

const historyRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "past-gigs",
  loaderDeps: gigViewDeps,
  loader: ({ deps, context, cause }) => {
    if (cause === "stay") return;
    warmQueries(
      context.queryClient,
      historyQueries({
        search: deps,
        location: context.location,
        now: new Date(),
        isAdmin: !!context.auth.user.is_gigpro_admin,
      }),
    );
  },
  component: lazyRouteComponent(() => import("domains/schedule/views/History"), "History"),
});

export const eventNetworkRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "event/$eventId/network/$",
  component: lazyRouteComponent(() => import("domains/schedule/views/NetworkEvent"), "NetworkEventView"),
});

export const eventMarketplaceRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "event/$eventId/marketplace/$",
  component: lazyRouteComponent(() => import("domains/schedule/views/MarketplaceEvent"), "MarketplaceEventView"),
});

export const applicantsRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "gigs/$gigId/applicants",
  validateSearch: applicantsSearchSchema,
  component: lazyRouteComponent(() => import("views/ListApplicants"), "ListApplicants"),
});

export const bulkReviewRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "gigs/$gigId/bulk-review",
  component: lazyRouteComponent(() => import("domains/gigs/views/BulkReview"), "BulkReviewView"),
});

export const gigAddRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "gigs-add",
  // The nav's Post, where R4 is on: V2 opens the chat beside it once it lands there.
  validateSearch: z.object({ from_nav: z.optional(z.boolean()) }),
  beforeLoad: async ({ context, params, search }) => {
    if (!isPlainSingle(search)) return;
    if (await postingV2On(context.queryClient, context.auth.user, Number(params.locationId))) {
      throw redirect({
        to: "/locations/$locationId/gigs-add/v2",
        params,
        search: search.from_nav ? { from_nav: true } : {},
        replace: true,
      });
    }
  },
  loaderDeps: postGigDeps,
  loader: ({ deps, context }) => {
    if (context.location)
      warmQueries(context.queryClient, postGigQueries({ location: context.location, search: deps }));
  },
  component: lazyRouteComponent(() => import("domains/postGig/views/Root"), "PostGigsRootView"),
});

// The posting page the chat drives ("Posting page V2"): both flags and the manager's switch, else today's form.
export const postShiftV2Route = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "gigs-add/v2",
  // Opened by the switch, or for a copy or a position: a draft left here earlier is offered (Continue draft or New
  // shift), never resumed silently. A copy or a position is filled once, then leaves the URL.
  validateSearch: z.object({
    from_switch: z.optional(z.boolean()),
    from_nav: z.optional(z.boolean()),
    position: z.optional(z.string()),
    copy_of: z.optional(z.int()),
  }),
  beforeLoad: async ({ context, params }) => {
    if (!(await postingV2On(context.queryClient, context.auth.user, Number(params.locationId)))) {
      throw redirect({ to: "/locations/$locationId/gigs-add", params, replace: true });
    }
  },
  component: lazyRouteComponent(() => import("domains/postGig/views/PostShiftV2"), "PostShiftV2View"),
});

const dashboardsRoute = createRoute({
  getParentRoute: () => lmOnlyRoute,
  path: "finance",
  component: lazyRouteComponent(
    () => import("domains/dashboards/views/MarketplaceRoot/Lazy"),
    "MarketplaceRootDashboardLayout",
  ),
});

const financeIndexRoute = createRoute({
  getParentRoute: () => dashboardsRoute,
  path: "/",
  validateSearch: z.object({ run: z.optional(z.uuid()) }),
  beforeLoad: async ({ context: { queryClient }, params }) => {
    const reportBuilderEnabled = await getFeatureFlag("billing-report-builder-web", { queryClient });
    if (!reportBuilderEnabled) {
      throw redirect({
        to: "/locations/$locationId/finance/$name",
        params: { ...params, name: MARKETPLACE_DASHBOARD_NAMES[0].path },
        replace: true,
      });
    }
  },
  loaderDeps: ({ search: { run } }) => ({ run }),
  loader: ({ context: { location, queryClient }, deps: { run } }) => {
    if (location) warmQueries(queryClient, financeIndexQueries({ location, runId: run }));
  },
  component: lazyRouteComponent(() => import("domains/dashboards/views/FinanceIndex"), "FinanceIndexView"),
});

export const dashboardDetailRoute = createRoute({
  getParentRoute: () => dashboardsRoute,
  path: "$name",
  component: lazyRouteComponent(() => import("domains/dashboards/views/Dashboard"), "DashboardView"),
});

const dashboardFallbackRoute = createRoute({
  getParentRoute: () => dashboardsRoute,
  path: "$",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/locations/$locationId/finance/$name",
      params: { locationId: params.locationId, name: MARKETPLACE_DASHBOARD_NAMES[0].path },
    });
  },
});

export const marketplaceRoutes = marketplaceLayoutRoute.addChildren([
  indexRoute,
  timeAndAttendanceRoute,
  gigsTimeAndAttendanceRoute,
  eventTimeAndAttendanceRoute,
  scheduleRoute,
  lmOnlyRoute.addChildren([
    homeRoute,
    getStartedRoute,
    postShiftRoute,
    postJobRoute,
    payRoute,
    historyRoute,
    eventNetworkRoute,
    eventMarketplaceRoute,
    applicantsRoute,
    bulkReviewRoute,
    gigAddRoute,
    postShiftV2Route,
    dashboardsRoute.addChildren([financeIndexRoute, dashboardDetailRoute, dashboardFallbackRoute]),
  ]),
]);
